import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from './ui/alert-dialog';
import { Copy, RefreshCw, Send, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${API_BASE_URL}/api`;

const EVENT_GROUPS = [
  {
    label: 'Tâches',
    events: [
      { value: 'task.created', label: 'Tâche créée' },
      { value: 'task.status_changed', label: 'Statut modifié' },
      { value: 'task.assigned', label: 'Tâche assignée' },
      { value: 'task.overdue', label: 'Tâche en retard', comingSoon: true },
      { value: 'task.deleted', label: 'Tâche supprimée' },
    ],
  },
  {
    label: 'Deals',
    events: [
      { value: 'deal.won', label: 'Deal gagné' },
      { value: 'deal.lost', label: 'Deal perdu' },
    ],
  },
  {
    label: 'Workspace',
    events: [
      { value: 'member.added', label: 'Membre ajouté' },
      { value: 'automation.triggered', label: 'Automation déclenchée', comingSoon: true },
    ],
  },
];

const WebhookModal = ({ open, onClose, webhook, workspaceId, boardId, level, onSaved }) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [secret, setSecret] = useState('');
  const [urlError, setUrlError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const isEdit = !!webhook;

  useEffect(() => {
    if (webhook) {
      setName(webhook.name);
      setUrl(webhook.url);
      setEvents(webhook.events || []);
      setIsActive(webhook.is_active);
      setSecret(webhook.secret || '');
    } else {
      setName('');
      setUrl('');
      setEvents([]);
      setIsActive(true);
      setSecret('');
    }
    setUrlError('');
  }, [webhook, open]);

  const validateUrl = (val) => {
    if (!val.startsWith('https://')) {
      setUrlError("L'URL doit utiliser HTTPS");
    } else {
      setUrlError('');
    }
  };

  const toggleEvent = (ev) => {
    setEvents(prev => prev.includes(ev) ? prev.filter(e => e !== ev) : [...prev, ev]);
  };

  const handleSave = async () => {
    if (urlError || !url.startsWith('https://')) {
      setUrlError("L'URL doit utiliser HTTPS");
      return;
    }
    if (!name.trim()) return;
    if (events.length === 0) {
      toast({ title: 'Sélectionnez au moins un événement', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      if (isEdit) {
        await axios.put(
          `${API}/webhooks/${webhook._id}`,
          { name, url, events, is_active: isActive },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API}/webhooks?workspace_id=${workspaceId}`,
          { name, url, events, is_active: isActive, level, board_id: boardId || null },
          { withCredentials: true }
        );
      }
      toast({ title: isEdit ? 'Webhook mis à jour' : 'Webhook créé' });
      onSaved();
      onClose();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Une erreur est survenue',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!isEdit) return;
    try {
      setTesting(true);
      await axios.post(
        `${API}/webhooks/${webhook._id}/test`,
        {},
        { withCredentials: true }
      );
      toast({ title: 'Payload de test envoyé' });
    } catch (err) {
      toast({ title: 'Erreur lors du test', variant: 'destructive' });
    } finally {
      setTesting(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    toast({ title: 'Secret copié' });
  };

  const handleRegenerateSecret = async () => {
    try {
      setRegenerating(true);
      const res = await axios.post(
        `${API}/webhooks/${webhook._id}/regenerate-secret`,
        {},
        { withCredentials: true }
      );
      setSecret(res.data.secret);
      toast({ title: 'Secret régénéré' });
    } catch {
      toast({ title: 'Erreur lors de la régénération', variant: 'destructive' });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le webhook' : 'Nouveau webhook'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="wh-name">Nom</Label>
            <Input
              id="wh-name"
              placeholder="ex: Zapier CRM"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>

          {/* URL */}
          <div className="space-y-1">
            <Label htmlFor="wh-url">URL (HTTPS uniquement)</Label>
            <Input
              id="wh-url"
              placeholder="https://hooks.zapier.com/..."
              value={url}
              onChange={e => { setUrl(e.target.value); validateUrl(e.target.value); }}
            />
            {urlError && <p className="text-xs text-red-500">{urlError}</p>}
          </div>

          {/* Events */}
          <div className="space-y-2">
            <Label>Événements</Label>
            {EVENT_GROUPS.map(group => (
              <div key={group.label}>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  {group.label}
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {group.events.map(ev => (
                    <label key={ev.value} className={`flex items-center gap-2 text-sm ${ev.comingSoon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <Checkbox
                        checked={events.includes(ev.value)}
                        onCheckedChange={() => !ev.comingSoon && toggleEvent(ev.value)}
                        disabled={ev.comingSoon}
                      />
                      <span>{ev.label}</span>
                      {ev.comingSoon && <span className="text-xs text-gray-400 ml-1">bientôt</span>}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Secret (edit only) */}
          {isEdit && secret && (
            <div className="space-y-1">
              <Label>Secret HMAC</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={secret}
                  className="font-mono text-xs bg-gray-50 dark:bg-gray-800"
                />
                <Button variant="outline" size="icon" onClick={handleCopySecret} title="Copier">
                  <Copy className="h-4 w-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="icon" disabled={regenerating} title="Régénérer">
                      {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Régénérer le secret ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Les intégrations utilisant l'ancien secret cesseront de fonctionner. Cette action est irréversible.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={handleRegenerateSecret}>Régénérer</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <Switch checked={isActive} onCheckedChange={setIsActive} id="wh-active" />
            <Label htmlFor="wh-active">Webhook actif</Label>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <div>
            {isEdit && (
              <Button variant="outline" onClick={handleTest} disabled={testing}>
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Envoyer un test
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Sauvegarder
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WebhookModal;
